import React from 'react';
import { ShoppingBag, ArrowRight } from 'lucide-react';

export interface StorefrontItem {
  id: string;
  name: string;
  amountMinor: number; // int64 integer minor units (WB-I7)
  currency: string;
  description: string;
}

export interface StorefrontCatalogViewProps {
  items: StorefrontItem[];
  onCheckout: (item: StorefrontItem) => void;
}

// formatMinorUnits formats integer minor currency units to decimal display without floating point math (WB-I7).
export function formatMinorUnits(minor: number, currency: string): string {
  const integerPart = Math.floor(minor / 100);
  const fractionPart = String(minor % 100).padStart(2, '0');
  return `${currency} ${integerPart}.${fractionPart}`;
}

// StorefrontCatalogView renders products and fee payment items with minor currency formatting.
export function StorefrontCatalogView({
  items,
  onCheckout,
}: StorefrontCatalogViewProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <ShoppingBag className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Storefront & Checkout Catalog</h2>
      </div>

      {items.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground border border-dashed border-border rounded-xl">
          <ShoppingBag className="h-10 w-10 mx-auto opacity-40 mb-2" />
          <p className="text-sm font-semibold text-foreground">No storefront items available</p>
          <p className="text-xs">Publish course fee structures in Suite S4 to make them available for online enrollment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="p-5 rounded-xl border border-border bg-card text-card-foreground shadow-sm flex flex-col justify-between min-h-[160px]"
            >
              <div>
                <h3 className="font-semibold text-foreground text-base">{item.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
              </div>
              <div className="pt-4 border-t border-border mt-4 flex items-center justify-between">
                <span className="font-bold text-sm text-foreground">
                  {formatMinorUnits(item.amountMinor, item.currency)}
                </span>
                <button
                  type="button"
                  onClick={() => onCheckout(item)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity min-h-[44px]"
                >
                  <span>Checkout</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
